// Supabase calls for public.areas and the tenant's Locations list. Kept apart
// from src/lib/areas.ts so the pure helpers stay unit-testable.
import { supabase } from "@/integrations/supabase/client";
import type { AreaRow } from "@/lib/areas";

// The table is newer than the generated types; the string cast keeps the
// builder usable until Lovable regenerates them.
const areasTable = () => supabase.from("areas" as never);

export async function fetchAreaRows(tenantId: string, opts: { includeUnpublished?: boolean } = {}): Promise<AreaRow[]> {
  let query = areasTable().select("*").eq("tenant_id", tenantId);
  if (!opts.includeUnpublished) query = query.eq("published", true);
  const { data, error } = await query.order("name");
  if (error) throw error;
  return (data ?? []) as unknown as AreaRow[];
}

export type AreaUpsert = {
  tenant_id: string;
  slug: string;
  name: string;
  published?: boolean;
  lat?: number | null;
  lng?: number | null;
  updated_by?: string | null;
  short_description?: string | null;
  tags?: string[];
  best_for?: string | null;
  vibe?: string | null;
  access?: string | null;
  location_bullets?: string[];
  known_for?: string[];
  seo_title?: string | null;
  seo_description?: string | null;
  seo_text?: string | null;
};

/** Insert or update one area; only the columns present are written. */
export async function upsertAreaRow(payload: AreaUpsert): Promise<void> {
  const { error } = await areasTable().upsert(payload as never, { onConflict: "tenant_id,slug" });
  if (error) throw error;
}

export async function deleteAreaRow(tenantId: string, slug: string): Promise<void> {
  const { error } = await areasTable().delete().eq("tenant_id", tenantId).eq("slug", slug);
  if (error) throw error;
}

interface TenantListRow {
  areas: string[];
  area_slugs: string[];
  primary_area_slug: string | null;
}

/**
 * Keep tenants.areas / area_slugs (the city's Locations list, which orders
 * and gates every area surface) in step with the areas table.
 */
export async function syncTenantLocation(
  tenantId: string,
  slug: string,
  name: string,
  mode: "add" | "rename" | "remove",
): Promise<void> {
  const { data, error } = await supabase
    .from("tenants")
    .select("areas, area_slugs, primary_area_slug")
    .eq("id", tenantId)
    .single();
  if (error || !data) throw error ?? new Error("Tenant not found");
  const row = data as TenantListRow;
  const slugs = [...row.area_slugs];
  const names = [...row.areas];
  const i = slugs.indexOf(slug);
  if (mode === "add") {
    if (i >= 0) return;
    slugs.push(slug);
    names.push(name);
  } else if (mode === "rename") {
    if (i < 0 || names[i] === name) return;
    names[i] = name;
  } else {
    if (i < 0) return;
    slugs.splice(i, 1);
    names.splice(i, 1);
  }
  const primary = slugs.includes(row.primary_area_slug ?? "") ? row.primary_area_slug : slugs[0] ?? "";
  const { error: upErr } = await supabase
    .from("tenants")
    .update({ areas: names, area_slugs: slugs, primary_area_slug: primary })
    .eq("id", tenantId);
  if (upErr) throw upErr;
}
